import type { ISecrEmissionData, ISecrReportFiguresGroup, IGeneratedSecrReportResponse } from '../../../../shared/services/generated-report/generated-report.model';

/** Label column width (% of table width) — same for summary and by-source tables so columns line up. */
export const SECR_REPORT_LABEL_COLUMN_PERCENT = 32;

const EPS = 0.02;

function n(v: number | null | undefined): number {
  if (v == null || Number.isNaN(Number(v))) return 0;
  return Number(v);
}

/** Safe emission row for missing intensity rows. */
export function emptyEmissionData(): ISecrEmissionData {
  return { uk: null as unknown as number, overseas: null as unknown as number, global: null as unknown as number };
}

export function hasScopeThreeData(e: ISecrEmissionData | undefined): boolean {
  if (!e) return false;
  return [e.uk, e.overseas, e.global].some((v) => v != null && Number.isFinite(Number(v)));
}

export function reportHasAnyScopeThreeFigures(report: IGeneratedSecrReportResponse): boolean {
  return report.reportDetails.some((d) => hasScopeThreeData(d.emissions?.scopeThree));
}

/**
 * Expected: Total (Location) = S1 + S2 location + (S3 if included in report).
 * Expected: Total (Market) = S1 + S2 market + (S3 if included in report).
 */
export function validateSecrSummaryTotals(
  d: ISecrReportFiguresGroup,
  includeScopeThreeInSum: boolean
): { ok: boolean; messages: string[] } {
  const messages: string[] = [];

  const checkSeg = (seg: 'uk' | 'overseas' | 'global', segLabel: string) => {
    const s1 = n(d.emissions?.scopeOne?.[seg]);
    const s2l = n(d.emissions?.scopeTwoLocation?.[seg]);
    const s2m = n(d.emissions?.scopeTwoMarket?.[seg]);
    const s3 = n(d.emissions?.scopeThree?.[seg]);
    const tl = n(d.emissions?.totalLocation?.[seg]);
    const tm = n(d.emissions?.totalMarket?.[seg]);
    const s3part = includeScopeThreeInSum ? s3 : 0;
    const expL = s1 + s2l + s3part;
    const expM = s1 + s2m + s3part;
    if (Math.abs(tl - expL) > EPS) {
      messages.push(
        `${segLabel} Total (Location-based): reported ${tl.toFixed(4)} vs expected ${expL.toFixed(4)} (S1+S2L${includeScopeThreeInSum ? '+S3' : ''})`
      );
    }
    if (Math.abs(tm - expM) > EPS) {
      messages.push(
        `${segLabel} Total (Market-based): reported ${tm.toFixed(4)} vs expected ${expM.toFixed(4)} (S1+S2M${includeScopeThreeInSum ? '+S3' : ''})`
      );
    }
  };

  checkSeg('uk', 'UK');
  checkSeg('overseas', 'Overseas');
  checkSeg('global', 'Total column');

  return { ok: messages.length === 0, messages };
}

export function logSecrSummaryValidationDev(periodName: string, result: { ok: boolean; messages: string[] }): void {
  const isDev =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
  if (isDev && !result.ok) {
    // eslint-disable-next-line no-console
    console.warn(`[SECR summary table] Total check for ${periodName}:`, result.messages);
  }
}
